<template>
    <layout :studio="studio" :cart="cart">
        <template #title>
            <h2 class="mt-3 mb-8 text-lg lg:mt-0 xl:text-5xl">Checkout</h2>
        </template>
        <p class="mb-2">
            <a class="button button-xs button-gray-outline" :href="route('studio.shop.cart.index', { studioSlug: studio.slug })" title="Back to Cart">
                <i class="fas fa-fw fa-arrow-left" title="Back to Cart"></i> Back to Cart
            </a>
        </p>
        <div class="bg-white p-4 shadow-lg">
            <div class="justify-between space-y-4 lg:flex lg:space-y-0 lg:space-x-4">
                <div class="flex-1">
                    <el-form ref="form" :model="customer" label-position="top">
                        <h3>Thank you for shopping at Brush Crazy.</h3>
                        <h4 class="mb-8">You have multiple ways to Paint Your Way to Fun!</h4>
                        <p>Choose to stay in our studio and artists will be available for help as you need it.</p>
                        <p>Prefer to paint at home? Take your project To-Gogh (To-Go) or have it delivered.</p>
                        <h5 class="mt-4 mb-6">Pick Up Options</h5>
                        <div class="mb-4 flex items-center space-x-4">
                            <button
                                v-for="(label, opt) in $t('cartCollectionTypes')"
                                :key="`collection-option-${opt}`"
                                type="button"
                                class="button button-sm button-turquoise"
                                :class="{ 'button-turquoise-outline': cart.collection_option !== opt }"
                                @click="cart.collection_option = opt"
                            >
                                {{ label }}
                            </button>
                        </div>
                        <p class="my-4">Choose your date first then pick your time.</p>
                        <div class="justify-between lg:flex lg:space-x-4">
                            <el-form-item label="Date" class="mb-0 flex-1">
                                <el-date-picker
                                    v-if="cart.collection_option === 'stay'"
                                    v-model="cart.collection_date"
                                    type="date"
                                    placeholder="Select Date"
                                    format="M/d/yyyy"
                                    value-format="yyyy-MM-dd"
                                    :picker-options="pickerStayOptions"
                                ></el-date-picker>
                                <el-date-picker
                                    v-else
                                    v-model="cart.collection_date"
                                    type="date"
                                    placeholder="Select Date"
                                    format="M/d/yyyy"
                                    value-format="yyyy-MM-dd"
                                    :picker-options="pickerOptions"
                                ></el-date-picker>
                            </el-form-item>
                            <transition name="slide">
                                <el-form-item v-if="cart.collection_date" v-loading="loadingAvailability" label="Time" class="mb-0 flex-1">
                                    <el-select v-model="cart.collection_time" no-data-text="No Time Available" placeholder="Select Time">
                                        <el-option
                                            v-for="(t, index) in getHoursList(cart.collection_date)"
                                            :key="`time-${index}`"
                                            :label="t"
                                            :value="t"
                                            :disabled="isNotAvailable(t)"
                                        ></el-option>
                                    </el-select>
                                </el-form-item>
                            </transition>
                        </div>
                        <h5 class="mt-4 mb-6">Recipient Information</h5>
                        <p class="my-4">Is this for you or someone else? If this is a gift for somebody else, choose "Someone Else" and enter their name.</p>
                        <el-radio-group v-model="recipient">
                            <el-radio label="self">Myself</el-radio>
                            <el-radio label="other">Someone Else</el-radio>
                        </el-radio-group>
                        <transition name="slide">
                            <el-form-item v-if="recipient === 'other'" label="Recipient Name" class="mt-4 max-w-xs">
                                <el-input v-model="cart.recipient" type="text"></el-input>
                            </el-form-item>
                        </transition>
                        <transition name="slide">
                            <div v-if="cart.collection_option === 'delivery'" key="delivery_address" class="mt-4">
                                <h5 class="mb-2">Delivery Information</h5>
                                <div class="justify-between xl:flex xl:space-x-4">
                                    <div class="flex-1">
                                        <el-form-item label="Street Address" class="w-full">
                                            <el-input v-model="delivery.street_address" type="text" placeholder="Street Address"></el-input>
                                        </el-form-item>
                                        <el-form-item v-if="showSuite" label="Apartment/Unit/Suite #" class="w-full">
                                            <el-input v-model="delivery.suite" type="text" placeholder="Apartment/Unit/Suite #"></el-input>
                                        </el-form-item>
                                        <el-form-item v-else class="w-full">
                                            <p class="mb-0 cursor-pointer text-turquoise-500 hover:text-turquoise-700" @click="showSuite = true">
                                                <i class="fas fa-fw fa-plus" title="Add Apartment/Unit/Suite #" aria-hidden="true"></i>
                                                Add Apartment/Unit/Suite #
                                            </p>
                                        </el-form-item>
                                    </div>
                                    <div class="flex-1">
                                        <div class="lg:flex lg:space-x-2">
                                            <el-form-item label="City" class="flex-1">
                                                <el-input v-model="delivery.locality" type="text" placeholder="City"></el-input>
                                            </el-form-item>
                                            <el-form-item label="State" class="flex-1">
                                                <el-select v-model="delivery.administrative_area_level_1" filterable placeholder="State">
                                                    <el-option
                                                        v-for="region in getRegionsForCountry(delivery.country_code)"
                                                        :key="region.shortCode"
                                                        :label="region.name"
                                                        :value="region.shortCode"
                                                    ></el-option>
                                                </el-select>
                                            </el-form-item>
                                        </div>
                                        <div class="lg:flex lg:space-x-2">
                                            <el-form-item label="Postal Code" class="flex-1">
                                                <el-input v-model="delivery.postal_code" type="text" placeholder="Postal Code"></el-input>
                                            </el-form-item>
                                            <el-form-item label="Country" class="flex-1">
                                                <el-select v-model="delivery.country_code" filterable placeholder="Country">
                                                    <el-option v-for="country in countries" :key="country.shortCode" :label="country.name" :value="country.shortCode"></el-option>
                                                </el-select>
                                            </el-form-item>
                                        </div>
                                    </div>
                                </div>
                                <p>Please have someone available for delivery. Brush Crazy is not responsible for unattended items delivered.</p>
                                <el-form-item label="Delivery Instructions">
                                    <el-input v-model="cart.delivery_instructions" type="textarea" autosize></el-input>
                                </el-form-item>
                            </div>
                        </transition>
                        <hr class="border border-turquoise-500" />
                        <h5 class="mt-4 mb-6">Billing Information</h5>
                        <template v-if="isLoggedIn()">
                            <div class="space-x-12 lg:flex">
                                <div class="flex-shrink-0">
                                    <p>{{ customer.first_name }} {{ customer.last_name }}</p>
                                    <p>{{ customer.email }}</p>
                                    <p>{{ customer.phone_number }}</p>
                                </div>
                                <div class="flex-shrink-0">
                                    <addr :entity="billing"></addr>
                                </div>
                                <div>
                                    <p class="text-sm">
                                        If this information is not correct, you need to
                                        <a :href="route('customer.account')" class="underline hover:no-underline">update your account</a>.
                                    </p>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="mb-4 items-center xl:flex xl:space-x-4">
                                <div class="flex-2 lg:flex lg:space-x-4">
                                    <el-form-item label="First Name" class="mb-4 flex-1">
                                        <el-input v-model="customer.first_name" type="text"></el-input>
                                    </el-form-item>
                                    <el-form-item label="Last Name" class="mb-4 flex-1">
                                        <el-input v-model="customer.last_name" type="text"></el-input>
                                    </el-form-item>
                                </div>
                                <el-form-item label="Phone Number" class="mb-4 min-w-40 flex-1">
                                    <el-input v-model="customer.phone_number" type="tel"></el-input>
                                </el-form-item>
                            </div>
                            <div class="mb-4 items-center xl:flex xl:space-x-4">
                                <el-form-item label="E-Mail Address" class="mb-4 flex-1">
                                    <el-input v-model="customer.email" type="email"></el-input>
                                </el-form-item>
                                <el-form-item label="Confirm E-Mail Address" class="mb-4 flex-1">
                                    <el-input v-model="customer.email_confirmation" type="email"></el-input>
                                </el-form-item>
                            </div>
                            <div class="justify-between xl:flex xl:space-x-4">
                                <div class="flex-1">
                                    <el-form-item label="Street Address" class="w-full">
                                        <el-input v-model="billing.street_address" type="text" placeholder="Street Address"></el-input>
                                    </el-form-item>
                                    <el-form-item v-if="showSuite" label="Apartment/Unit/Suite #" class="w-full">
                                        <el-input v-model="billing.suite" type="text" placeholder="Apartment/Unit/Suite #"></el-input>
                                    </el-form-item>
                                    <el-form-item v-else class="w-full">
                                        <p class="mb-0 cursor-pointer text-turquoise-500 hover:text-turquoise-700" @click="showSuite = true">
                                            <i class="fas fa-fw fa-plus" title="Add Apartment/Unit/Suite #" aria-hidden="true"></i>
                                            Add Apartment/Unit/Suite #
                                        </p>
                                    </el-form-item>
                                </div>
                                <div class="flex-1">
                                    <div class="lg:flex lg:space-x-2">
                                        <el-form-item label="City" class="flex-1">
                                            <el-input v-model="billing.locality" type="text" placeholder="City"></el-input>
                                        </el-form-item>
                                        <el-form-item label="State" class="flex-1">
                                            <el-select v-model="billing.administrative_area_level_1" filterable placeholder="State">
                                                <el-option
                                                    v-for="region in getRegionsForCountry(billing.country_code)"
                                                    :key="region.shortCode"
                                                    :label="region.name"
                                                    :value="region.shortCode"
                                                ></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>
                                    <div class="lg:flex lg:space-x-2">
                                        <el-form-item label="Postal Code" class="flex-1">
                                            <el-input v-model="billing.postal_code" type="text" placeholder="Postal Code"></el-input>
                                        </el-form-item>
                                        <el-form-item label="Country" class="flex-1">
                                            <el-select v-model="billing.country_code" filterable placeholder="Country">
                                                <el-option v-for="country in countries" :key="country.shortCode" :label="country.name" :value="country.shortCode"></el-option>
                                            </el-select>
                                        </el-form-item>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </el-form>
                </div>
                <div class="w-76">
                    <div class="mb-4 rounded-lg border border-gray-300 p-4 shadow-md">
                        <h5 class="mb-0 text-center">Sale Summary</h5>
                        <div class="divide-y divide-gray-300">
                            <p v-for="item in cart.items.filter((i) => i.isInStock)" :key="`item-${item.id}`" class="items-center justify-end pt-3 text-right lg:flex">
                                <span class="mr-auto text-left">
                                    {{ item.product }}<br />
                                    <template v-if="!['Apparel', 'Merchandise'].includes(item.category) || !/^.*Brush/.test(item.category)">
                                        <template v-if="item.option_id">
                                            <small class="italic">{{ item.option }}</small>
                                        </template>
                                        <template v-else>
                                            <small class="italic">Custom Project</small>
                                        </template>
                                    </template>
                                </span>
                                <span class="ml-4 w-16 font-bold">{{ item.subtotal | asCurrency }}</span>
                            </p>
                            <p
                                v-for="(brush, index) in cart.brushes.filter((b) => !/Rental/.test(b.name))"
                                :key="`brush-${index}`"
                                class="items-center justify-end pt-3 text-right lg:flex"
                            >
                                <span class="mr-auto text-left"> {{ brush.name }}<br /> </span>
                                <span class="ml-4 w-16 font-bold">{{ brush.amount | asCurrency }}</span>
                            </p>
                        </div>
                        <div class="mt-3 border-t border-gray-300 pt-3">
                            <p class="items-center justify-end text-right lg:flex">
                                <span>Subtotal:</span>
                                <span class="ml-4 w-16 font-bold">{{ subtotal | asCurrency }}</span>
                            </p>
                            <template
                                v-if="
                                    coupon_code &&
                                    discount.amount &&
                                    ((discount.amount.amount && discount.amount.amount > 0) || (discount.amount.percent && discount.amount.percent > 0))
                                "
                            >
                                <p class="items-center justify-end text-right lg:flex">
                                    <span
                                        ><button type="button" class="button button-xs button-red-outline" @click="removeDiscount"><i class="fas fa-fw fa-times-circle"></i></button>
                                        Coupon ({{ coupon_code }}):</span
                                    >
                                    <span class="ml-4 w-16 font-bold">{{ disc | asCurrency }}</span>
                                </p>
                            </template>
                            <template v-if="studio.sales_tax.length > 0">
                                <p class="items-center justify-end text-right lg:flex">
                                    <span>Tax ({{ { percent: taxRate * 100 } | asPercent }}):</span>
                                    <span class="ml-4 w-16 font-bold">{{ tax | asCurrency }}</span>
                                </p>
                            </template>
                            <p
                                v-for="(brush, index) in cart.brushes.filter((b) => /Rental/.test(b.name))"
                                :key="`brush-rental-${index}`"
                                class="items-center justify-end border-t border-gray-300 pt-3 text-right lg:flex"
                            >
                                <span class="mr-auto text-left"> {{ brush.name }}<br /> </span>
                                <span class="ml-4 w-16 font-bold">{{ brush.amount | asCurrency }}</span>
                            </p>
                            <transition name="slide">
                                <template v-if="cart.collection_option === 'delivery'">
                                    <p v-if="deliveryFee.isZero()" key="calculate-delivery-fee">
                                        <el-button type="danger" size="small" :disabled="!hasCompleteAddress()" :loading="calculatingDeliveryFee" @click="calculateDeliveryFee"
                                            >Calculate Delivery Fee</el-button
                                        >
                                    </p>
                                    <p v-else key="delivery-fee" class="items-center justify-end text-right lg:flex">
                                        <span>Delivery Fee:</span>
                                        <span class="ml-4 w-16 font-bold">{{ deliveryFee | asCurrency }}</span>
                                    </p>
                                </template>
                            </transition>
                            <p class="items-center justify-end border-t border-gray-300 pt-3 text-right lg:flex">
                                <span>Total:</span>
                                <span class="ml-4 w-16 font-bold">{{ total | asCurrency }}</span>
                            </p>
                            <p v-for="(p, index) in payments" :key="`payment-${index}`" class="items-center justify-end text-right lg:flex">
                                <span
                                    ><button type="button" class="button button-xs button-red-outline" @click="removePayment(index)"><i class="fas fa-fw fa-times-circle"></i></button>
                                    {{ p.name }}:</span
                                >
                                <span class="ml-4 w-16 font-bold">{{ p.amount | asCurrency }}</span>
                            </p>
                            <p class="items-center justify-end text-right lg:flex">
                                <span>Balance Due:</span>
                                <span class="ml-4 w-16 font-bold">{{ balance | asCurrency }}</span>
                            </p>
                        </div>
                    </div>
                    <hr class="border border-turquoise-500" />
                    <div class="my-8">
                        <p class="mb-4">Do you have a coupon code? Add it here before your payment to receive the discount. Type it exactly as shown.</p>
                        <div class="flex">
                            <input id="coupon_code" v-model="coupon_code" type="text" name="coupon_code" title="Coupon Code" placeholder="Coupon Code" />
                            <button type="button" class="button button-turquoise flex-shrink-0 rounded-l-none" @click="applyDiscount">Apply</button>
                        </div>
                    </div>
                    <template v-if="((cart.collection_option === 'delivery' && !deliveryFee.isZero()) || cart.collection_option !== 'delivery') && !balance.isZero()">
                        <hr class="border border-turquoise-500" />
                        <h5 class="uppercase">Payment Information</h5>
                        <div v-if="isLoggedIn()">
                            <template v-if="!(paintMoney.isZero() && customerCard.isZero())">
                                <p class="font-bold">Use your VIP Card or Paint Money</p>
                                <div class="items-center justify-between lg:flex lg:space-x-4">
                                    <div class="mt-4 flex-1">
                                        <button
                                            v-if="!paintMoney.isZero()"
                                            type="button"
                                            class="button button-watermelon w-full"
                                            title="Apply Paint Money"
                                            @click="startPayment('Paint Money', paintMoney)"
                                        >
                                            <span class="text-xl font-bold">{{ paintMoney | asCurrency }}</span>
                                            <br /><span class="text-xs">Paint Money</span>
                                        </button>
                                    </div>
                                    <div class="mt-4 flex-1">
                                        <button
                                            v-if="!customerCard.isZero()"
                                            type="button"
                                            class="button button-tangerine w-full"
                                            title="Apply VIP Card"
                                            @click="startPayment('Customer Card', customerCard)"
                                        >
                                            <span class="text-xl font-bold">{{ customerCard | asCurrency }}</span>
                                            <br /><span class="text-xs">VIP Card</span>
                                        </button>
                                    </div>
                                </div>
                            </template>
                            <template v-if="shared.auth.user.birthdayCoupon">
                                <p class="mt-4 font-bold">Use your Birthday Coupon</p>
                                <div class="items-center justify-between lg:flex lg:space-x-4">
                                    <div class="mt-4 flex-1">
                                        <button
                                            type="button"
                                            class="button button-tangerine w-full"
                                            :disabled="shared.auth.user.birthdayCoupon.isUseless || payments.findIndex((d) => d.name === 'Birthday Coupon') !== -1"
                                            :data-tooltip="shared.auth.user.birthdayCoupon.isUseless ? 'Birthday Coupon Already Used' : undefined"
                                            @click="birthdayCoupon"
                                        >
                                            <i class="fas fa-fw fa-birthday-cake"></i> Birthday
                                        </button>
                                    </div>
                                    <div class="mt-4 flex-1"></div>
                                </div>
                            </template>
                        </div>
                        <div class="items-center justify-between lg:flex lg:space-x-4">
                            <div class="mt-4 flex-1">
                                <button type="button" class="button button-blue w-full" @click="startPayment('Credit Card')"><i class="fas fa-fw fa-credit-card"></i> Credit</button>
                            </div>
                            <div class="mt-4 flex-1">
                                <button type="button" class="button button-indigo w-full" @click="startPayment('Gift Card')"><i class="fas fa-fw fa-gift-card"></i> Gift Card</button>
                            </div>
                        </div>
                    </template>
                    <p v-else-if="cart.collection_option === 'delivery'">Click the Calculate Delivery Fee button above to add delivery to your order.</p>
                    <button
                        v-if="payments.length > 0 || balance.isZero()"
                        v-loading="paying"
                        type="button"
                        class="button button-green mt-4 w-full"
                        :disabled="(cart.collection_option === 'delivery' && deliveryFee.isZero()) || !balance.isZero()"
                        @click="submit"
                    >
                        <i class="fas fa-fw fa-shopping-cart"></i> Checkout
                    </button>
                </div>
            </div>
        </div>
        <portal to="modals">
            <el-dialog :visible.sync="showAddPaymentDialog" :width="isMobile ? '50%' : '30%'" @closed="resetPayment">
                <h4 slot="title" class="text-left">Balance Due: {{ balance | asCurrency }}</h4>
                <el-form ref="form" :model="payment" label-position="top">
                    <component
                        :is="payment.type"
                        v-model="payment.card_number"
                        :customer="shared.auth.user || {}"
                        :payments="payments"
                        :can-submit="paymentCanSubmit"
                        :payment="payment"
                        @valid="paymentCanBeAdded = true"
                        @invalid="paymentCanBeAdded = false"
                        @balance="adjustPaymentAmount"
                        @submit="addPayment"
                    ></component>
                    <el-form-item label="Amount">
                        <money-input v-model="payment.amount" :disabled="['GiftCard', 'CauseCard'].includes(payment.type) && !paymentCanBeAdded"></money-input>
                    </el-form-item>
                </el-form>
                <div slot="footer" class="dialog-footer">
                    <el-button :loading="submitting" @click="resetPayment">Cancel</el-button>
                    <el-button type="primary" :loading="submitting" :disabled="!paymentCanBeAdded" @click="finishPayment">Add Payment</el-button>
                </div>
            </el-dialog>
        </portal>
    </layout>
</template>

<script>
    import Bugsnag from '@bugsnag/js';
    import Dinero from 'dinero.js';
    import { DateTime, Interval } from 'luxon';
    import axios from '@/axios';
    import countries from '@/countries';
    import parseDate from '@/date';
    import address from '@/mixins/address';
    import geocodeAddress from '@/mixins/geocodeAddress';
    import hasCart from '@/mixins/hasCart';
    import hasOperatingHours from '@/mixins/hasOperatingHours';
    import asCurrency from '@/filters/asCurrency';
    import asPercent from '@/filters/asPercent';
    import asTime from '@/filters/asTime';
    import asTimeRange from '@/filters/asTimeRange';
    import Address from '@/components/Address.vue';
    import MoneyInput from '@/backend/components/MoneyInput.vue';
    import CreditCard from '@/backend/components/Sale/Payment/CreditCard.vue';
    import CustomerCard from '@/backend/components/Sale/Payment/CustomerCard.vue';
    import GiftCard from '@/backend/components/Sale/Payment/GiftCard.vue';
    import PaintMoney from '@/backend/components/Sale/Payment/PaintMoney.vue';
    import Layout from '@/frontend/components/Studio/Shop/Layout.vue';

    export default {
        name: 'Checkout',
        components: {
            Layout,
            addr: Address,
            MoneyInput,
            CreditCard,
            CustomerCard,
            GiftCard,
            PaintMoney,
        },
        filters: {
            asCurrency,
            asPercent,
            asTime,
            asTimeRange,
        },
        mixins: [address, geocodeAddress, hasCart, hasOperatingHours],
        props: {
            studio: {
                required: true,
                type: Object,
            },
            entity: {
                required: true,
                type: Object,
            },
        },
        data() {
            return {
                pickerOptions: {
                    disabledDate: this.disabledDates,
                },
                pickerStayOptions: {
                    disabledDate: this.disabledStayDates,
                },
                showSuite: false,
                countries,
                recipient: 'self',
                customer: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone_number: '',
                    street_number: null,
                    route: null,
                    suite: null,
                    locality: null,
                    administrative_area_level_1: null,
                    postal_code: null,
                    country_code: 'US',
                    timezone: 'America/Denver',
                    lat: null,
                    lng: null,
                },
                billing: {
                    street_address: '',
                    street_number: '',
                    route: '',
                    suite: '',
                    locality: '',
                    administrative_area_level_1: '',
                    postal_code: '',
                    country_code: 'US',
                    lat: '',
                    lng: '',
                    timezone: 'America/Denver',
                },
                delivery: {
                    street_address: '',
                    street_number: '',
                    route: '',
                    suite: '',
                    locality: '',
                    administrative_area_level_1: '',
                    postal_code: '',
                    country_code: 'US',
                    lat: '',
                    lng: '',
                    timezone: 'America/Denver',
                },
                deliveryFee: new Dinero(),
                calculatingDeliveryFee: false,
                loadingAvailability: true,
                availability: {},
                coupon_code: '',
                discount: {
                    name: '',
                    type: '',
                    basis: '',
                    amount: {
                        amount: 0,
                        currency: 'USD',
                    },
                    option_type: '',
                    option_id: '',
                },
                payment: {
                    name: null,
                    type: null,
                    amount: {
                        amount: 0,
                        currency: 'USD',
                    },
                    card_number: '',
                },
                payments: [],
                showAddPaymentDialog: false,
                paymentCanBeAdded: false,
                paymentCanSubmit: false,
                submitting: false,
                paying: false,
            };
        },
        computed: {
            qty() {
                return this.inStockItems.reduce((carry, item) => carry + item.quantity, 0);
            },
            subtotal() {
                if (this.cart.items.length === 0) {
                    return new Dinero();
                }

                const brushes = this.cart.brushes.filter((brush) => !/Rental/.test(brush.name)).reduce((carry, brush) => carry.add(new Dinero(brush.amount)), new Dinero());

                return this.inStockItems.reduce((carry, item) => carry.add(new Dinero(item.subtotal)), brushes);
            },
            taxRate() {
                return this.studio.sales_tax.reduce((carry, tax) => carry + tax.amount, 0);
            },
            disc() {
                if (this.discount.basis === 'percent') {
                    return this.subtotal.multiply(this.discount.amount.percent / 10_000, 'HALF_UP');
                }

                if (this.discount.amount && this.discount.amount.amount > 0) {
                    return new Dinero(this.discount.amount);
                }

                return new Dinero();
            },
            tax() {
                const subtotal = this.subtotal.subtract(this.disc);

                return this.studio.sales_tax.reduce((carry, tax) => carry.add(subtotal.multiply(tax.amount / 100, 'HALF_UP')), new Dinero());
            },
            total() {
                return this.subtotal
                    .subtract(this.disc)
                    .add(this.cart.brushes.filter((brush) => /Rental/.test(brush.name)).reduce((carry, brush) => carry.add(new Dinero(brush.amount)), new Dinero()))
                    .add(this.tax)
                    .add(this.deliveryFee);
            },
            balance() {
                return this.total.subtract(this.payments.reduce((carry, payment) => carry.add(new Dinero(payment.amount)), new Dinero()));
            },
            paintMoney() {
                if (!this.isLoggedIn()) {
                    return new Dinero();
                }

                return new Dinero(this.shared.auth.user.rewards).subtract(
                    this.payments
                        .filter((payment) => payment.type === 'PaintMoney' && !['Birthday Coupon', '$5 Free'].includes(payment.name))
                        .reduce((carry, payment) => carry.add(new Dinero(payment.amount)), new Dinero()),
                );
            },
            customerCard() {
                if (!this.isLoggedIn()) {
                    return new Dinero();
                }

                return new Dinero(this.shared.auth.user.card).subtract(
                    this.payments.filter((payment) => payment.type === 'CustomerCard').reduce((carry, payment) => carry.add(new Dinero(payment.amount)), new Dinero()),
                );
            },
        },
        watch: {
            'cart.collection_option': {
                handler() {
                    this.cart.collection_date = null;
                    this.cart.collection_time = null;
                },
            },
            'cart.collection_date': {
                handler() {
                    this.cart.collection_time = null;
                    this.loadingAvailability = true;

                    axios
                        .get(
                            route('studio.shop.availability', {
                                studioSlug: this.studio.slug,
                                date: this.cart.collection_date,
                                option: this.cart.collection_option,
                            }),
                        )
                        .then(
                            (response) => {
                                this.availability = response.data.data;
                                if (Array.isArray(this.availability)) {
                                    this.availability = {};
                                }
                            },
                            () => {
                                this.availability = {};
                            },
                        )
                        .finally(() => {
                            this.loadingAvailability = false;
                        });
                },
            },
            delivery: {
                handler() {
                    this.deliveryFee = new Dinero();
                },
                deep: true,
            },
        },
        created() {
            if (this.isLoggedIn()) {
                this.customer.first_name = this.shared.auth.user.name.split(' ')[0] || '';
                this.customer.last_name = this.shared.auth.user.name.split(' ')[1] || '';
                this.customer.email = this.shared.auth.user.email;
                this.customer.phone_number = this.shared.auth.user.phone_number;
                this.billing.street_address = `${this.shared.auth.user.street_number} ${this.shared.auth.user.route}`.trim();
                this.billing.street_number = this.shared.auth.user.street_number || '';
                this.billing.route = this.shared.auth.user.route || '';
                this.billing.suite = this.shared.auth.user.suite || '';
                this.billing.locality = this.shared.auth.user.locality || '';
                this.billing.administrative_area_level_1 = this.shared.auth.user.administrative_area_level_1 || '';
                this.billing.postal_code = this.shared.auth.user.postal_code || '';
                this.billing.country_code = this.shared.auth.user.country_code || 'US';
                this.billing.lat = this.shared.auth.user.lat || '';
                this.billing.lng = this.shared.auth.user.lng || '';
                this.billing.timezone = this.shared.auth.user.timezone || 'America/Denver';
                this.delivery.street_address = `${this.shared.auth.user.street_number} ${this.shared.auth.user.route}`.trim();
                this.delivery.street_number = this.shared.auth.user.street_number || '';
                this.delivery.route = this.shared.auth.user.route || '';
                this.delivery.suite = this.shared.auth.user.suite || '';
                this.delivery.locality = this.shared.auth.user.locality || '';
                this.delivery.administrative_area_level_1 = this.shared.auth.user.administrative_area_level_1 || '';
                this.delivery.postal_code = this.shared.auth.user.postal_code || '';
                this.delivery.country_code = this.shared.auth.user.country_code || 'US';
                this.delivery.lat = this.shared.auth.user.lat || '';
                this.delivery.lng = this.shared.auth.user.lng || '';
                this.delivery.timezone = this.shared.auth.user.timezone || 'America/Denver';
            }
        },
        methods: {
            hasCompleteAddress(entity) {
                const addr = entity || this.delivery;

                return (
                    ((!!addr.street_number && !!addr.route) || !!addr.street_address) &&
                    !!addr.locality &&
                    !!addr.administrative_area_level_1 &&
                    !!addr.postal_code &&
                    !!addr.country_code
                );
            },
            checkIfDisabled(time) {
                function pad(number) {
                    if (number < 10) {
                        return `0${number}`;
                    }

                    return number;
                }

                return this.isHoliday(`${time.getUTCFullYear()}-${pad(time.getUTCMonth() + 1)}-${pad(time.getUTCDate())}`);
            },
            disabledDates(time) {
                return this.checkIfDisabled(time) || time.getTime() < DateTime.local();
            },
            disabledStayDates(time) {
                return this.checkIfDisabled(time) || time.getTime() < DateTime.local().minus({ days: 1 });
            },
            getDateObjectFrom(d) {
                return parseDate(d);
            },
            getHoursList(d) {
                if (this.cart.collection_option === 'delivery') {
                    return this.getDeliveryHoursList(d);
                }

                return this.getOperatingHoursList(d);
            },
            getDeliveryHoursList(d) {
                if (this.studio.contents.delivery_hours === null) {
                    return [];
                }

                const date = this.getDateObjectFrom(d);

                return this.studio.contents.delivery_hours[date.weekdayLong.toLocaleLowerCase()]
                    .map((h) => {
                        const [openHour, openMinute, closeHour, closeMinute] = this.parseOperatingHours(h),
                            open = date.set({ hour: openHour, minute: openMinute }),
                            close = date.set({ hour: closeHour, minute: closeMinute });

                        return this.$options.filters.asTimeRange(open, close);
                    })
                    .filter((h) => h !== null);
            },
            getOperatingHoursList(d) {
                if (this.studio.operating_hours === null || !this.isOpenOn(d)) {
                    return [];
                }

                const hours = this.parseOperatingHoursForDate(d),
                    open = DateTime.fromISO(hours[0]),
                    close = DateTime.fromISO(hours[1]),
                    interval = Interval.fromDateTimes(open, close),
                    dateParts = d.split('-'),
                    selectedDate = ['year', 'month', 'day'].reduce((carry, value, index) => {
                        carry[value] = dateParts[index];

                        return carry;
                    }, {}),
                    now = DateTime.local();

                return interval
                    .splitBy({ hours: 1 })
                    .map((t) => this.$options.filters.asTime(t.start))
                    .filter((t) => DateTime.fromFormat(t, 't').set(selectedDate) >= now);
            },
            isHoliday(date) {
                const exception = this.studio.operating_hours.exceptions[date];

                return exception && exception.hours.length === 0;
            },
            isNotAvailable(time) {
                if (this.studio.operating_hours !== null && this.isHoliday(this.cart.collection_date)) {
                    return false;
                }

                const availabilitySlots =
                    this.studio.contents && this.studio.contents.availability_slots ? this.studio.contents.availability_slots[this.cart.collection_option] : undefined;

                if (availabilitySlots === undefined || availabilitySlots === null) {
                    return false;
                }

                return this.availability[time] && this.availability[time].collection_count > (availabilitySlots || 3);
            },
            calculateDeliveryFee() {
                if (!this.hasCompleteAddress(this.delivery)) {
                    this.$notify.error({
                        title: this.$t('Error'),
                        message: 'You must fill out your delivery address for us to be able to deliver to you.',
                        duration: 4000,
                    });
                }

                this.calculatingDeliveryFee = true;

                const { number: streetNumber, street: route } = this.splitAddress(this.delivery.street_address);
                this.delivery.street_number = streetNumber;
                this.delivery.route = route;

                this.geocodeAddress(this.delivery)
                    .then((addr) => {
                        this.cart.street_number = addr.street_number;
                        this.cart.route = addr.route;
                        this.cart.suite = addr.suite;
                        this.cart.locality = addr.locality;
                        this.cart.administrative_area_level_1 = addr.administrative_area_level_1;
                        this.cart.postal_code = addr.postal_code;
                        this.cart.country_code = addr.country_code;
                        this.cart.timezone = addr.timezone;
                        this.cart.lat = addr.lat;
                        this.cart.lng = addr.lng;
                        this.$nextTick(() => {
                            const service = new google.maps.DistanceMatrixService(),
                                destination = this.buildAddress(this.delivery);

                            if (!/[A-Za-z]+/.test(destination)) {
                                this.$notify.error({
                                    title: this.$t('Error'),
                                    message: 'You must fill out your delivery address first.',
                                    duration: 6000,
                                });
                                return;
                            }

                            service.getDistanceMatrix(
                                {
                                    origins: [this.buildAddress(this.studio)],
                                    destinations: [destination],
                                    travelMode: google.maps.TravelMode.DRIVING,
                                    unitSystem: google.maps.UnitSystem.IMPERIAL,
                                },
                                (results, status) => {
                                    if (status === 'OK' && results.rows.length > 0 && results.rows[0].elements.length > 0) {
                                        if (this.studio.contents.delivery_fees) {
                                            const miles = Math.ceil(results.rows[0].elements[0].distance.value * 0.000_621_37);
                                            let fee = new Dinero(this.studio.contents.delivery_fees.base);
                                            if (miles > this.studio.contents.delivery_fees.base_miles) {
                                                fee = fee.add(
                                                    new Dinero(this.studio.contents.delivery_fees.per_mile).multiply(
                                                        miles - this.studio.contents.delivery_fees.base_miles,
                                                        'HALF_AWAY_FROM_ZERO',
                                                    ),
                                                );
                                            }
                                            this.deliveryFee = fee;
                                        }
                                        this.calculatingDeliveryFee = false;
                                        return;
                                    }

                                    this.$notify.error({
                                        title: this.$t('Error'),
                                        message:
                                            'There was a problem calculating your delivery fee. Check your address and try again. If this problem persists, contact your local studio for assistance.',
                                        duration: 6000,
                                    });

                                    Bugsnag.notify(new Error(status), (event) => {
                                        event.severity = 'error';
                                        event.addMetadata('contextData', {
                                            origin: this.buildAddress(this.studio),
                                            destination,
                                            results,
                                            status,
                                        });
                                    });
                                },
                            );
                        });
                    }, this.$root.handleError)
                    .then(() => {
                        this.calculatingDeliveryFee = false;
                    });
            },
            applyDiscount() {
                axios.get(route('api.coupons.search', { code: this.coupon_code })).then((response) => {
                    this.$root.handleSuccess(response);

                    if (response.data.status === 'success') {
                        const { data } = response.data;
                        this.discount = {
                            name: data.name,
                            type: data.discount,
                            basis: data.discount,
                            amount: data.amount,
                            option_type: 'promotion',
                            option_id: data.id,
                        };
                    }
                }, this.$root.handleError);
            },
            removeDiscount() {
                this.discount = {
                    name: '',
                    type: '',
                    basis: '',
                    amount: {
                        amount: 0,
                        currency: 'USD',
                    },
                    option_type: '',
                    option_id: '',
                };
            },
            birthdayCoupon() {
                this.payments.push({
                    name: 'Birthday Coupon',
                    type: 'PaintMoney',
                    amount: {
                        amount: 1000,
                        currency: 'USD',
                    },
                    card_number: `BIRTHDAY-${this.shared.auth.user.id}`,
                });
            },
            startPayment(type, balance) {
                this.payment.name = type;
                this.payment.type = type.replace(/\s/, '');
                const paymentBalance = balance ? new Dinero(balance) : new Dinero(),
                    saleBalance = this.balance;

                if (type === 'Credit Card' || type === 'Vault') {
                    this.payment.amount = saleBalance.toObject();
                } else {
                    this.payment.amount = saleBalance.lessThanOrEqual(paymentBalance) ? saleBalance.toObject() : paymentBalance.toObject();
                }
                this.showAddPaymentDialog = true;
            },
            adjustPaymentAmount(value) {
                const balance = new Dinero(value);

                if (balance.lessThan(new Dinero(this.payment.amount))) {
                    this.payment.amount = balance.toObject();
                }
            },
            finishPayment() {
                this.submitting = true;
                this.paymentCanSubmit = true;
            },
            addPayment() {
                this.payments.push({ ...this.payment });
                this.resetPayment();
            },
            removePayment(index) {
                this.payments.splice(index, 1);
            },
            resetPayment() {
                this.showAddPaymentDialog = false;
                this.paymentCanBeAdded = false;
                this.paymentCanSubmit = false;
                this.submitting = false;
                this.payment = {
                    name: null,
                    type: null,
                    amount: {
                        amount: 0,
                        currency: 'USD',
                    },
                    card_number: '',
                };
            },
            submit() {
                this.paying = true;

                const { number: streetNumber, street: route } = this.splitAddress(this.billing.street_address);
                this.billing.street_number = streetNumber;
                this.billing.route = route;

                this.geocodeAddress(this.billing).then(
                    (addr) => {
                        this.customer.street_number = addr.street_number;
                        this.customer.route = addr.route;
                        this.customer.suite = addr.suite;
                        this.customer.locality = addr.locality;
                        this.customer.administrative_area_level_1 = addr.administrative_area_level_1;
                        this.customer.postal_code = addr.postal_code;
                        this.customer.country_code = addr.country_code;
                        this.customer.timezone = addr.timezone;
                        this.customer.lat = addr.lat;
                        this.customer.lng = addr.lng;
                        this.$nextTick(() => {
                            this.pay();
                        });
                    },
                    (error) => {
                        if (error === 'ZERO_RESULTS') {
                            this.customer.street_number = this.billing.street_number;
                            this.customer.route = this.billing.route;
                            this.customer.suite = this.billing.suite;
                            this.customer.locality = this.billing.locality;
                            this.customer.administrative_area_level_1 = this.billing.administrative_area_level_1;
                            this.customer.postal_code = this.billing.postal_code;
                            this.customer.country_code = this.billing.country_code;
                            this.customer.timezone = this.billing.timezone;
                            this.customer.lat = this.billing.lat;
                            this.customer.lng = this.billing.lng;
                            this.$nextTick(() => {
                                this.pay();
                            });
                            return;
                        }

                        this.paying = false;
                        this.$root.handleError(error);
                    },
                );
            },
            pay() {
                axios
                    .post(route('studio.shop.checkout', { studioSlug: this.studio.slug }), {
                        _token: window.Laravel.csrfToken,
                        customer: this.customer,
                        cart: { ...this.cart, items: this.cart.items.filter((i) => i.isInStock).map((i) => ({ ...i, photo: undefined, urls: undefined })) },
                        deliveryFee: this.deliveryFee.toObject(),
                        discount: this.discount,
                        payments: this.payments,
                    })
                    .then(
                        (response) => {
                            this.paying = false;
                            this.$root.handleSuccess(response, 'Finalized successfully!');
                            if (response.data.status === 'success') {
                                this.$nextTick(() => {
                                    window.location = route('receipts.show', { sale: response.data.data.id });
                                });
                            }
                        },
                        (error) => {
                            this.paying = false;
                            this.$root.handleError(error);
                        },
                    );
            },
        },
    };
</script>

<style>
    .el-radio-group {
        display: flex;
    }

    .slide-enter-active {
        -moz-transition-duration: 0.3s;
        -webkit-transition-duration: 0.3s;
        -o-transition-duration: 0.3s;
        transition-duration: 0.3s;
        -moz-transition-timing-function: ease-in;
        -webkit-transition-timing-function: ease-in;
        -o-transition-timing-function: ease-in;
        transition-timing-function: ease-in;
    }

    .slide-leave-active {
        -moz-transition-duration: 0.3s;
        -webkit-transition-duration: 0.3s;
        -o-transition-duration: 0.3s;
        transition-duration: 0.3s;
        -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
        -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
        -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
        transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    }

    .slide-enter-to,
    .slide-leave {
        max-height: 50px;
        overflow: hidden;
    }

    .slide-enter,
    .slide-leave-to {
        overflow: hidden;
        max-height: 0;
    }
</style>
